/** 표기 규칙. 앱과 웹이 같은 값을 같은 모양으로 써야 셀러가 두 화면을 같은 것으로 읽는다.
 *
 * 순수 TypeScript 만 쓴다 — `Intl` 은 Workers·Hermes 양쪽에 있지만 로케일 데이터가
 * 런타임마다 달라 같은 입력이 다르게 보일 수 있어 쓰지 않는다.
 */

/** `₩189` — 목업 표기. 천 단위 구분은 넣는다(`₩27,000`). */
export function formatKrw(amount: number): string {
  // 유한한 수가 아니면 **던진다. 화면 문자열로 덮지 않는다.**
  //
  // **이 자리는 도달하지 않는다** (2026-09-08 전 호출부 실측). 금액이 들어오는 경로는 셋뿐이고
  // 셋 다 유한하다:
  //  1. `cutoutPrice`·`remakePrice`(옛 이름 `cutoutCost`·`retryCost` 는 2026-09-13 에 지웠다)는 `장수 × 상수`
  //     곱셈이다. 이 패키지에 금액을 만드는 나눗셈이 없어서 `0/0` 이 생길 자리가 없다.
  //     2026-09-13 에 곱하는 상수가 원가 ₩27 에서 청구가 ₩500 으로 바뀌었지만 **곱셈이라는
  //     사실은 그대로**라 이 근거는 유지된다.
  //  2. 서버가 주는 금액 필드(`billedAmount`·`netAmount`·`totalAmount`·`refundedAmount`)는
  //     SQL 에서 `COALESCE(SUM(...), 0)` 을 통과하고, 원장 컬럼이
  //     `"amount" INTEGER NOT NULL CHECK ("amount" >= 0)` 이다. 행이 없어도 `null` 이 아니라 `0`.
  //  3. 나머지 호출부는 목업 상수다.
  // 그래서 여기 닿는 값은 세상의 상태가 아니라 **계약 위반**이다. 계약 위반은 셀러가 아니라
  // 우리가 봐야 하고, 던지면 개발·CI 에서 걸린다.
  //
  // 옛 구현은 `₩—` 를 돌려줬다(폐기, 2026-09-08). 두 가지가 틀렸다.
  //  * 줄표는 셀러가 보는 문자열에서 금지다.
  //  * 더 무거운 쪽: **기호가 사유를 대신했다.** 금액 칸에 뜻 없는 글리프가 뜨면 셀러는 읽을
  //    것이 없고, 계산 결함은 화면에서 가려진 채로 남는다. 같은 종류를 `apps/web` 의 작업
  //    목록에서도 잡았다(값이 없을 때 기호 하나를 찍던 자리).
  // `₩0` 으로 대신할 수도 없다. **이 제품에서 `₩0` 은 참인 값이다**(`돌아간 금액 ₩0`,
  // PRD 7.6.21a). 모르는 것을 참인 값으로 적으면 그건 틀린 사실이다.
  //
  // 낱말을 새로 짓지도 않았다. PRD 7.5.9 용어 표에 "금액을 모른다"에 해당하는 개념이 없고,
  // 규칙 1 이 즉석에서 고르는 것을 금지한다. **도달하지 않는 상태에 셀러의 낱말을 배정하면
  // 표에 쓸 수 없는 항목이 하나 늘 뿐이다.**
  if (!Number.isFinite(amount)) {
    throw new TypeError(`formatKrw: 금액이 유한한 수가 아니다 (${String(amount)})`);
  }

  const rounded = Math.round(amount);
  // 이 `-` 는 **자리표시자가 아니라 산술 부호다.** 줄표 훑기가 이 줄을 잡더라도 바꾸지 마라.
  // (원장 제약상 음수도 실제로 오지 않는다. 오면 `-₩189` 로 정직하게 적는다.)
  const sign = rounded < 0 ? "-" : "";
  const digits = Math.abs(rounded).toString();
  let out = "";
  for (let i = 0; i < digits.length; i += 1) {
    if (i > 0 && (digits.length - i) % 3 === 0) out += ",";
    out += digits[i];
  }
  return `${sign}₩${out}`;
}

/** `7 / 8장` — 촬영 카운트 필과 진행률 표시가 같은 모양을 쓴다. */
export function formatCount(current: number, total: number): string {
  return `${current} / ${total}장`;
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}

/** 목록 한 줄의 시각. 목업 A-10 이 쓰는 규칙 그대로다 —
 * 방금이면 `방금`, 오늘이면 `14:20`, 그 전이면 `09-05`.
 *
 * 상대 시각을 "3분 전"처럼 늘려 쓰지 않는 이유: 목록의 보조줄은 모노스페이스로 세로로
 * 훑는 자리라 길이가 들쭉날쭉하면 비교가 안 된다(PRD 7.3). */
export function formatListTime(iso: string, now: Date = new Date()): string {
  const parsed = parseServerDate(iso);
  // **여기는 `formatKrw` 와 달리 던지지 않는다.** 두 자리의 성격이 다르다.
  //
  //  * 도달한다. 서버 시각(`createdAt`·`updatedAt`)은 `NOT NULL` 이라 안전하지만, 앱의
  //    기기 저장소가 주는 시각은 아니다. `apps/mobile/src/lib/job-store.ts` 의 검증이
  //    `typeof record.startedAt === "string"` 까지만 보므로, 옛 판이나 깨진 캐시가 아무
  //    문자열이나 들고 올 수 있다.
  //  * 빈 문자열은 **기호가 아니라 없음**이다. 이 값이 나가는 자리는 보조줄의 시각 조각이고,
  //    빠지면 남은 문구가 그대로 뜻을 갖는다(`8장 올림`). 금액 칸과 달리 그 줄이 답하는
  //    질문을 시각이 혼자 지고 있지 않다.
  // 시각을 못 읽었다는 사실을 셀러에게 말할 필요가 있다면 그건 이 함수가 아니라 그 줄을
  // 조립하는 화면의 판단이다(빈 조각 때문에 공백이 겹치는 자리는 화면 담당 소관).
  if (!parsed) return "";

  const diffMs = now.getTime() - parsed.getTime();
  if (diffMs >= 0 && diffMs < 60_000) return "방금";

  const sameDay =
    parsed.getFullYear() === now.getFullYear() &&
    parsed.getMonth() === now.getMonth() &&
    parsed.getDate() === now.getDate();

  if (sameDay) return `${pad2(parsed.getHours())}:${pad2(parsed.getMinutes())}`;

  const md = `${pad2(parsed.getMonth() + 1)}-${pad2(parsed.getDate())}`;
  // 올해가 아니면 연도를 적는다(PRD 7.5.13 규칙 3). 연도가 없으면 작년 12월 작업이 올해
  // 것으로 읽힌다. **이 함수는 줄 단위로 판단해도 된다** — 폭이 이미 갈래마다 달라서
  // (`방금`·`14:20`·`09-05`) 열의 폭이 고르다는 성질을 애초에 갖고 있지 않다.
  // 두 자리 연도는 쓰지 않는다(규칙 5).
  if (parsed.getFullYear() !== now.getFullYear()) return `${parsed.getFullYear()}-${md}`;
  return md;
}

/** `09-06 14:25`. 날짜와 시각을 **언제나 둘 다** 적는다. 상대 표기(`방금`)를 쓰지 않는다.
 *
 * **`formatListTime` 과 합치지 않았다. 표시 규칙이 실제로 다르다.**
 *
 * | | `formatListTime` | `formatDateTime` |
 * |---|---|---|
 * | 답하는 물음 | 언제쯤인가 | 정확히 언제인가 |
 * | 오늘 것 | `14:20` (날짜를 버린다) | `09-06 14:20` |
 * | 1분 안 | `방금` | `09-06 14:25` |
 * | 폭 | 갈래마다 다르다 | 언제나 11자 |
 *
 * **`formatListTime` 이 정보를 버리는 것은 그 자리가 목록이라서다.** 세로로 훑는 열은 폭이
 * 고정되어야 비교가 되고, 같은 날 것끼리는 날짜가 겹쳐 무의미하다(PRD 7.3). **이 함수가
 * 쓰이는 두 자리는 그 전제가 둘 다 깨진다.**
 *
 *  1. **`A-12` 이력 타임라인.** 세 줄 고정이라 `언제` 줄이 그 물음을 혼자 진다
 *     (`JobEventView`). 그리고 **한 화면에 같은 날 여러 건이 서고 날을 넘겨서도 이어진다.**
 *     목록 규칙을 쓰면 오늘 것만 날짜가 사라져 한 열에 `14:25` 와 `09-05` 가 섞인다.
 *  2. **`A-32` 의 `09-06 09:47에 끝났습니다`.** 시각이 열이 아니라 **문장 안에 들어간다.**
 *     여기서 상대 표기는 문법이 깨진다(`방금에 끝났습니다`). 목록용 갈래를 문장에 끼울 수
 *     없다는 것이 두 함수를 가르는 가장 단단한 근거다.
 *
 * **연도는 부르는 쪽이 켠다**(`opts.year`, PRD 7.5.13 규칙 4). 이 함수가 줄마다 스스로
 * 판단하면 **한 열에 켜진 줄과 꺼진 줄이 섞인다** — 위 1번이 막으려던 바로 그 상태다.
 * 그래서 세로 모노 열은 **열 단위로** 켠다. 한 줄이라도 올해가 아니면 그 열 전부 켠다.
 * `formatListTime` 이 줄 단위로 판단해도 되는 것과 갈리는 지점이고, 이유는 그쪽이 폭이
 * 고르다는 성질을 애초에 갖고 있지 않기 때문이다.
 *
 * **"언제나 11자"를 잃는 것에 대해**: 위 표가 지키려던 것은 글자 수가 아니라 **한 열의 모든
 * 줄이 같은 정보를 적는 것**이다(1번이 그렇게 적고 있다). `opts.year` 를 열 단위로 켜면 그
 * 성질은 그대로다. 어긋난 폭은 보기 나쁜 것이고, **없는 연도는 화면이 거짓말을 하는 것**이다.
 *
 * **빈 문자열 계약은 `formatListTime` 과 같다.** 못 읽은 시각은 `""` 이고 조립하는 쪽이
 * 흡수한다. 근거는 위 `formatListTime` 안의 주석에 적었다. **바꾸지 마라.** 앱의 호출부가
 * 이미 이 계약에 맞춰 문장을 두 갈래로 두고 있다(`A-32` 는 조각이 비면 `끝났습니다` 로
 * 읽히게 하고, 빈 자리를 기호나 배지로 채우지 않는다). */
export interface DateTimeOptions {
  /** 연도를 적는다. **열 단위로 켠다** — 한 줄이라도 올해가 아니면 그 열 전부 켠다.
   *  줄마다 스스로 판단하게 두면 한 열에 두 모양이 섞인다(PRD 7.5.13 규칙 4). */
  year?: boolean;
}

export function formatDateTime(iso: string, opts: DateTimeOptions = {}): string {
  const parsed = parseServerDate(iso);
  if (!parsed) return "";

  // 두 자리 연도를 쓰지 않는다(규칙 5). 목업이 어디서도 쓰지 않는다.
  const y = opts.year ? `${parsed.getFullYear()}-` : "";
  const date = `${y}${pad2(parsed.getMonth() + 1)}-${pad2(parsed.getDate())}`;
  return `${date} ${pad2(parsed.getHours())}:${pad2(parsed.getMinutes())}`;
}

/** 한 열에 연도를 켤지 판정한다. **줄이 아니라 열이 판단 단위다**(PRD 7.5.13 규칙 4).
 *
 * 화면이 그 열의 시각을 전부 넘기면, 하나라도 올해가 아닐 때 `true` 를 돌려준다. 그 값을
 * 열의 모든 줄에 같은 값으로 넘겨야 모양이 섞이지 않는다. 못 읽은 시각은 판정에서 빠진다
 * (빈 문자열 계약과 같은 이유로, 읽지 못한 것은 사실을 말하지 않는다). */
export function needsYear(isos: readonly string[], now: Date = new Date()): boolean {
  const thisYear = now.getFullYear();
  return isos.some((iso) => {
    const parsed = parseServerDate(iso);
    return parsed !== null && parsed.getFullYear() !== thisYear;
  });
}

/** 서버 시각 문자열(D1 `CURRENT_TIMESTAMP` = `2026-09-06 14:25:31`, UTC)을 읽는다.
 * Safari/Hermes 는 이 형태를 파싱하지 못해 `Invalid Date` 가 되므로 ISO 로 맞춰준다.
 *
 * ⚠️ 오프셋이 없으면 UTC 로 본다 — 로컬로 읽으면 KST 기준 9시간이 조용히 어긋난다.
 * ⚠️ 아는 형식이 아니면 `null`. 런타임마다 파싱 범위가 달라 앱과 웹이 갈라진다. */
const SERVER_DATE_RE =
  /^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}:\d{2}(?:\.\d+)?)(Z|[+-]\d{2}:?\d{2})?$/;

export function parseServerDate(value: string): Date | null {
  if (!value) return null;

  const match = SERVER_DATE_RE.exec(value);
  if (!match) return null;

  const [, datePart, timePart, offset] = match;
  const date = new Date(`${datePart}T${timePart}${offset ?? "Z"}`);
  return Number.isNaN(date.getTime()) ? null : date;
}
